import React, { useState, useMemo, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  CssBaseline, 
  Button,
  Menu,
  MenuItem,
  Box,
  Alert,
  Collapse,
  Paper
} from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import OnboardingSteps from './components/hire/OnboardingSteps';
import CRMDashboard from './components/dashboard/CRMDashboard';
import CRMTickets from './components/crm/CRMTickets';
import OwnerDashboard from './components/dashboard/OwnerDashboard';
import OwnerDashboardDemo from './components/dashboard/OwnerDashboardDemo';
import FrontendSMSManager from './components/messaging/FrontendSMSManager';
import LoginPage from './components/auth/LoginPage';
import AuthCallback from './components/auth/AuthCallback';
import NewHireLoginPage from './components/auth/NewHireLoginPage';
import NotFound from './components/common/NotFound';
import { ForgotPasswordForm } from './components/auth/ForgotPasswordForm';
import { ResetPasswordForm } from './components/auth/ResetPasswordForm';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AdminOwnerRoute } from './components/auth/AdminOwnerRoute';
import ReleaseNotes from './components/common/ReleaseNotes';
import Tutorials from './components/common/Tutorials';
import { TicketForm } from './components/crm/TicketForm';
import { supabase } from './config/supabase';
import './App.css';
import { GlobalHelpButton } from './components/common/GlobalHelpButton';

// App Content component to access location
const AppContent = () => {
  const [mode, setMode] = useState<'light' | 'dark'>('light');
  const [user, setUser] = useState<any>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false); // Change this to false to disable the notification
  const location = useLocation();

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClose = () => {
    setNotificationOpen(false);
  };

  useEffect(() => {
    const checkUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setUser(user);
        
        if (user) {
          // Get the user's profile to check their role
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', user.id)
            .single();
          
          if (!profileError && profile) {
            setUserRole(profile.role);
          }
        }
      } catch (error) {
        setUser(null);
        setUserRole(null);
      }
    };

    // Check user on mount and when location changes
    checkUser();
  }, [location]);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      handleClose(); // Close the menu
      window.location.reload();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: '#1976d2',
          },
          secondary: {
            main: '#dc004e',
          },
          background: {
            default: mode === 'light' ? '#f5f5f5' : '#121212',
            paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
          },
        },
        components: {
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'light' ? '#1976d2' : '#1e1e1e',
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'light' ? '#ffffff' : '#1e1e1e',
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: '12px',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                padding: '10px 24px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: mode === 'dark'
                    ? '0 8px 25px rgba(0, 0, 0, 0.3)'
                    : '0 8px 25px rgba(102, 126, 234, 0.4)',
                },
              },
              contained: {
                background: mode === 'dark'
                  ? 'linear-gradient(135deg, #3a3a3a 0%, #4a4a4a 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#ffffff',
                boxShadow: mode === 'dark'
                  ? '0 4px 15px rgba(0, 0, 0, 0.2)'
                  : '0 4px 15px rgba(102, 126, 234, 0.3)',
                border: 'none',
                '&:hover': {
                  background: mode === 'dark'
                    ? 'linear-gradient(135deg, #4a4a4a 0%, #5a5a5a 100%)'
                    : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: mode === 'dark'
                    ? '0 8px 25px rgba(0, 0, 0, 0.4)'
                    : '0 8px 25px rgba(102, 126, 234, 0.5)',
                },
                '&:disabled': {
                  background: mode === 'dark'
                    ? 'linear-gradient(135deg, #2a2a2a 0%, #3a3a3a 100%)'
                    : 'linear-gradient(135deg, #cccccc 0%, #e0e0e0 100%)',
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                  transform: 'none',
                  boxShadow: 'none',
                },
              },
              outlined: {
                borderColor: mode === 'dark' ? '#4a4a4a' : '#667eea',
                color: mode === 'dark' ? '#ffffff' : '#667eea',
                background: 'transparent',
                borderWidth: '2px',
                '&:hover': {
                  background: mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(102, 126, 234, 0.05)',
                  borderColor: mode === 'dark' ? '#6a6a6a' : '#764ba2',
                  transform: 'translateY(-2px)',
                  boxShadow: mode === 'dark'
                    ? '0 8px 25px rgba(0, 0, 0, 0.2)'
                    : '0 8px 25px rgba(102, 126, 234, 0.2)',
                },
              },
              text: {
                color: mode === 'dark' ? '#ffffff' : '#667eea',
                '&:hover': {
                  background: mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(102, 126, 234, 0.05)',
                  transform: 'translateY(-1px)',
                },
              },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  background: mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(102, 126, 234, 0.08)',
                },
              },
            },
          },
        },
      }),
    [mode],
  );

  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="fixed" sx={{
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%)',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 20px rgba(0, 0, 0, 0.5)'
          : '0 4px 20px rgba(102, 126, 234, 0.3)',
        borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)'}`,
        zIndex: (theme) => theme.zIndex.drawer + 1,
        width: '100%',
        left: 0,
        right: 0,
      }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            alignItems: 'center',
            fontWeight: 700,
            letterSpacing: '0.5px',
          }}>
            <Link 
              to="/" 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                textDecoration: 'none',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <img 
                src="/globe-life-logo.svg" 
                alt="Globe Life Logo" 
                style={{ 
                  height: '32px', 
                  width: 'auto',
                  filter: theme.palette.mode === 'dark' ? 'brightness(1.2)' : 'brightness(1)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                }}
              />
            </Link>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {user && userRole === 'owner' && location.pathname === '/leaderdashboard' && (
              <Button
                component={Link}
                to="/tutorials"
                color="inherit"
                startIcon={<OndemandVideoIcon />}
                sx={{
                  mr: 1,
                  color: '#ffffff',
                  background: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: '12px',
                  px: 2,
                  py: 0.8,
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  '&:hover': {
                    color: '#ffffff',
                    background: 'rgba(255, 255, 255, 0.25)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Tutorials
              </Button>
            )}
            <IconButton 
              onClick={toggleColorMode} 
              color="inherit"
              sx={{
                background: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                '&:hover': {
                  background: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.15)'
                    : 'rgba(255, 255, 255, 0.3)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
            {user && (
              <>
                <IconButton
                  size="large"
                  edge="end"
                  color="inherit"
                  aria-label="menu"
                  onClick={handleMenu}
                  sx={{ 
                    ml: 1,
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    '&:hover': {
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.15)'
                        : 'rgba(255, 255, 255, 0.3)',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  sx={{
                    '& .MuiPaper-root': {
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, #2a2a2a 0%, #3a3a3a 100%)'
                        : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                        : '0 8px 32px rgba(102, 126, 234, 0.15)',
                      border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.2)'}`,
                      borderRadius: '12px',
                      mt: 1,
                    },
                    '& .MuiMenuItem-root': {
                      fontWeight: 600,
                      '&:hover': {
                        background: theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(102, 126, 234, 0.1)',
                      },
                    },
                  }}
                >
                  {userRole === 'owner' && (
                    <MenuItem 
                      onClick={() => {
                        handleClose();
                        window.location.href = '/sms-manager';
                      }}
                    >
                      SMS Manager
                    </MenuItem>
                  )}
                  {(userRole === 'admin' || userRole === 'owner') && (
                    <MenuItem 
                      onClick={() => {
                        handleClose();
                        window.location.href = '/release-notes';
                      }}
                    >
                      Release Notes
                    </MenuItem>
                  )}
                  <MenuItem 
                    onClick={() => {
                      handleClose();
                      setHelpDialogOpen(true);
                    }}
                  >
                    Help
                  </MenuItem>
                  <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Notification Ribbon */}
      <Collapse in={notificationOpen && user && userRole !== 'new_hire' && location.pathname !== '/onboarding'}>
        <Box sx={{ 
          position: 'fixed',
          top: '64px', // Position below AppBar
          left: '16px',
          right: '16px',
          zIndex: (theme) => theme.zIndex.appBar - 1,
          px: 2,
          py: 1,
        }}>
          <Paper 
            elevation={3} 
            sx={{ 
              background: theme.palette.mode === 'dark'
                ? 'rgba(102, 126, 234, 0.15)'
                : 'rgba(102, 126, 234, 0.1)',
              borderRadius: 2,
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(102, 126, 234, 0.3)' : 'rgba(102, 126, 234, 0.2)'}`,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Alert
              severity="info"
              sx={{
                background: 'transparent',
                color: theme.palette.mode === 'dark' ? '#ffffff' : '#1a1a1a',
                fontWeight: 600,
                py: 1,
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  color: theme.palette.mode === 'dark' ? '#ffffff' : '#1a1a1a',
                },
                '& .MuiAlert-message': {
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1rem',
                },
              }}
              action={
                <IconButton
                  size="small"
                  onClick={handleNotificationClose}
                  sx={{
                    color: theme.palette.mode === 'dark' ? '#ffffff' : '#1a1a1a',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.1)' 
                        : 'rgba(102, 126, 234, 0.1)',
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              🎉 New Feature is live! Calendly SMS Manager
            </Alert>
          </Paper>
        </Box>
      </Collapse>
      
      <Box sx={{ 
        mt: notificationOpen && user && userRole !== 'new_hire' && location.pathname !== '/onboarding' ? '112px' : '64px', // Account for AppBar + notification height
        pt: 2, // Add normal top-level spacing
        pb: 2, // Add bottom spacing for consistency
      }}>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<NewHireLoginPage />} />
          <Route path="/admin-login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/new-hire-login" element={<Navigate to="/login" replace />} />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute requiredRole="admin">
                <CRMDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/leaderdashboard" 
            element={
              <ProtectedRoute requiredRole="owner">
                <OwnerDashboard />
              </ProtectedRoute>
            } 
          />
          <Route path="/onboarding" element={<OnboardingSteps />} />
          <Route 
            path="/tickets" 
            element={
              <ProtectedRoute requiredRole="ticketmaster">
                <CRMTickets />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/sms-manager" 
            element={
              <AdminOwnerRoute>
                <FrontendSMSManager />
              </AdminOwnerRoute>
            } 
          />
          <Route 
            path="/release-notes" 
            element={
              <AdminOwnerRoute>
                <ReleaseNotes />
              </AdminOwnerRoute>
            } 
          />
          <Route 
            path="/tutorials" 
            element={
              <ProtectedRoute requiredRole="owner">
                <Tutorials />
              </ProtectedRoute>
            } 
          />
          <Route path="/forgot-password" element={<ForgotPasswordForm />} />
          <Route path="/reset-password" element={<ResetPasswordForm />} />
          <Route path="/demo-dashboard" element={<OwnerDashboardDemo />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Box>
      <GlobalHelpButton />
      
      {/* Help Dialog */}
      <TicketForm
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
      />
    </ThemeProvider>
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
